import { client } from '@/api/client'
import type { components } from '@/api/typeApi'
import type {
  CreateClusterRequest,
  ClusterDto,
  ExtendedClusterDto,
  CreateClusterResponse,
  ClusterRow,
  ServerForClusterRow,
  UpdateClusterRequest,
} from '../domain/types'
import { mapClusterDtoToClusterRow } from '../domain/types'
import { parseServerResources } from '@/features/Servers/infrastructure/api'

type LightNodeDto = {
  address: string
  cc?: string | null
  cluster?: string | null
  createdAt: string
  dc?: string | null
  id: string
  name: string
  nodeType: components['schemas']['NodeTypeDto']
  providerId: string
  publicKey: string
  resources?: null | components['schemas']['NodeInfoSpec']
  resourcesFetchedAt?: string | null
  status: components['schemas']['NodeStatusDto']
  talosVersion?: string | null
  updatedAt: string
}

function mapNodeToServerForCluster(node: LightNodeDto): ServerForClusterRow {
  const parsedResources = node.resources
    ? parseServerResources(node.resources)
    : null

  return {
    ...node,
    disks: parsedResources?.disks.map((disk) => disk) ?? [],
    interfaces: parsedResources?.interfaces.map((disk) => disk) ?? [],
    nodeAddresses: parsedResources?.node_addresses ?? [],
    lastTalosConnection: node.resourcesFetchedAt,
  }
}

export const clustersApi = {
  getClusters: async (): Promise<ClusterRow[]> => {
    const { data, error } = await client.GET('/v1/clusters')

    if (error) {
      throw new Error(`Failed to get clusters: ${error}`)
    }

    const { data: datacenters, error: datacentersError } =
      await client.GET('/v1/datacenters')

    if (datacentersError) {
      throw new Error(`Failed to get datacenters: ${datacentersError}`)
    }

    const clustersDto = data || []
    return clustersDto.map(mapClusterDtoToClusterRow(datacenters))
  },

  getClustersDirect: async (): Promise<ExtendedClusterDto[]> => {
    const { data, error } = await client.GET('/v1/clusters')

    if (error) {
      throw new Error(`Failed to get clusters: ${error}`)
    }

    return data || []
  },

  createCluster: async (
    requestData: CreateClusterRequest,
  ): Promise<CreateClusterResponse> => {
    const { data, error } = await client.POST('/v1/clusters', {
      body: requestData,
    })

    if (error) {
      throw new Error(`Failed to create cluster: ${error}`)
    }

    if (!data) {
      throw new Error('Failed to create cluster: no data returned')
    }

    return data
  },

  getCluster: async (clusterId: string): Promise<ClusterDto> => {
    const { data, error } = await client.GET('/v1/clusters/{cluster_id}', {
      params: { path: { cluster_id: clusterId } },
    })

    if (error) {
      throw new Error(`Failed to get cluster: ${error}`)
    }

    if (!data) {
      throw new Error('Failed to get cluster: no data returned')
    }

    return data
  },

  updateCluster: async (clusterId: string, body: UpdateClusterRequest) => {
    // The refusal body is {"error": "<string>"} and is not modeled in the
    // spec, so the caller reads it untyped off the raw result — as with subnets.
    return client.PATCH('/v1/clusters/{cluster_id}', {
      params: { path: { cluster_id: clusterId } },
      body,
    })
  },

  /**
   * TEMPORARY (owner's word, 2026-08-10): this action and its UI button exist
   * to run the one-off underlay→ul provider-network rename across the live
   * fleet. After the fleet migration the owner decides whether it stays.
   */
  applyClusterConfig: async (clusterId: string): Promise<void> => {
    // The 409 answer carries no body, and openapi-fetch would run it through
    // `response.json()` — read as text and decide by status.
    const { response } = await client.POST(
      '/v1/clusters/{cluster_id}/config/apply',
      {
        params: { path: { cluster_id: clusterId } },
        parseAs: 'text',
      },
    )

    if (response.status === 409) {
      throw new Error(
        'The cluster is still going through its state machine and will apply the config on its own.',
      )
    }

    if (!response.ok) {
      throw new Error('Failed to re-apply the cluster config.')
    }
  },

  getServersForCluster: async (): Promise<ServerForClusterRow[]> => {
    const { data, error } = await client.GET('/v1/nodes/extended', {
      params: {
        query: {
          pagination: { page: 1, per_page: 1000 },
          nodeType: 'unknown',
          status: 'maintenance',
        },
      },
    })

    if (error) {
      throw new Error(`Failed to get nodes: ${error}`)
    }

    const nodes = data || []
    return nodes.items.map(mapNodeToServerForCluster)
  },
}
